import React from 'react';
import { Grid3X3, RefreshCw, AlertCircle } from 'lucide-react';
import { useActivityHeatmap } from '../../../hooks/useAnalytics';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

function getHeatColor(value: number, max: number): string {
  if (max === 0 || value === 0) return 'bg-gray-100 dark:bg-gray-800';
  const intensity = value / max;
  if (intensity > 0.8) return 'bg-blue-700 text-white';
  if (intensity > 0.6) return 'bg-blue-500 text-white';
  if (intensity > 0.4) return 'bg-blue-400 text-white';
  if (intensity > 0.2) return 'bg-blue-200 text-blue-800';
  return 'bg-blue-100 text-blue-600';
}

function formatHour(h: number): string {
  if (h === 0) return '12a';
  if (h < 12) return `${h}a`;
  if (h === 12) return '12p';
  return `${h - 12}p`;
}

interface ActivityHeatmapWidgetProps {
  className?: string;
}

export function ActivityHeatmapWidget({ className = '' }: ActivityHeatmapWidgetProps) {
  const { data, isLoading, error, refetch } = useActivityHeatmap();

  if (isLoading) {
    return (
      <div className={`bg-white rounded-xl border border-gray-200 p-6 ${className} dark:bg-gray-800 dark:border-gray-700`}>
        <div className="animate-pulse space-y-3">
          <div className="h-5 bg-gray-200 rounded w-1/3 dark:bg-gray-700" />
          <div className="h-48 bg-gray-100 rounded dark:bg-gray-800" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white rounded-xl border border-gray-200 p-6 ${className} dark:bg-gray-800 dark:border-gray-700`}>
        <div className="text-center text-gray-500 dark:text-gray-400">
          <AlertCircle className="w-8 h-8 mx-auto mb-2 text-red-400" />
          <p className="text-sm">Failed to load activity heatmap</p>
          <button onClick={() => refetch()} className="text-xs text-blue-600 mt-1 hover:underline">Retry</button>
        </div>
      </div>
    );
  }

  const rows = data?.rows || [];
  const maxValue = data?.peakValue || 1;
  const allZeros = rows.length > 0 && maxValue === 0;

  // Compact state when heatmap has data rows but all values are zero
  if (allZeros) {
    return (
      <div className={`bg-white rounded-xl border border-gray-200 ${className} dark:bg-gray-800 dark:border-gray-700`}>
        <div className="flex items-center justify-between px-5 py-4">
          <div className="flex items-center gap-2">
            <Grid3X3 className="w-5 h-5 text-gray-300" />
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Activity Heatmap</h3>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400 dark:text-gray-500">No activity recorded for this period</span>
            <button onClick={() => refetch()} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded" title="Refresh">
              <RefreshCw className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl border border-gray-200 ${className} dark:bg-gray-800 dark:border-gray-700`}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
        <div className="flex items-center gap-2">
          <Grid3X3 className="w-5 h-5 text-blue-500" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Activity Heatmap</h3>
        </div>
        <div className="flex items-center gap-3">
          {data && (
            <span className="text-[10px] text-gray-400 dark:text-gray-500">
              Peak: {data.peakDay} {formatHour(data.peakHour)} ({data.peakValue} tickets)
            </span>
          )}
          <button onClick={() => refetch()} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded" title="Refresh">
            <RefreshCw className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
          </button>
        </div>
      </div>

      {/* Heatmap Grid */}
      <div className="p-4 overflow-x-auto">
        {rows.length === 0 ? (
          <div className="text-center py-8 text-gray-400 dark:text-gray-500">
            <Grid3X3 className="w-8 h-8 mx-auto mb-2" />
            <p className="text-sm">No activity data available</p>
          </div>
        ) : (
          <div className="min-w-[600px]">
            {/* Hour labels */}
            <div className="flex mb-1 pl-10">
              {HOURS.filter((_, i) => i % 3 === 0).map((h) => (
                <div key={h} className="text-[9px] text-gray-400 text-center dark:text-gray-500" style={{ width: `${(3 / 24) * 100}%` }}>
                  {formatHour(h)}
                </div>
              ))}
            </div>

            {/* Rows */}
            {rows.map((row, dayIdx) => (
              <div key={row.day} className="flex items-center gap-1 mb-0.5">
                <div className="w-8 text-[10px] text-gray-500 text-right pr-1 flex-shrink-0 dark:text-gray-400">
                  {DAYS[dayIdx] || row.day.slice(0, 3)}
                </div>
                <div className="flex flex-1 gap-px">
                  {row.hours.map((val, hIdx) => (
                    <div
                      key={hIdx}
                      className={`flex-1 h-5 rounded-sm ${getHeatColor(val, maxValue)} flex items-center justify-center cursor-default transition-colors`}
                      title={`${DAYS[dayIdx] || row.day} ${formatHour(hIdx)}: ${val} tickets`}
                    >
                      {val > 0 && maxValue > 10 ? (
                        <span className="text-[7px] font-medium">{val}</span>
                      ) : val > 0 ? (
                        <span className="text-[8px] font-medium">{val}</span>
                      ) : null}
                    </div>
                  ))}
                </div>
                <div className="w-8 text-[10px] text-gray-400 text-right flex-shrink-0 dark:text-gray-500">
                  {row.total}
                </div>
              </div>
            ))}

            {/* Legend */}
            <div className="flex items-center justify-end gap-1 mt-3 pr-9">
              <span className="text-[9px] text-gray-400 mr-1 dark:text-gray-500">Less</span>
              {['bg-gray-100 dark:bg-gray-800', 'bg-blue-100', 'bg-blue-200', 'bg-blue-400', 'bg-blue-500', 'bg-blue-700'].map((c) => (
                <div key={c} className={`w-3 h-3 rounded-sm ${c}`} />
              ))}
              <span className="text-[9px] text-gray-400 ml-1 dark:text-gray-500">More</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
